import React from 'react';
import PropTypes from 'prop-types';
import { Alert } from '@mui/material';
import { Icon } from './Icon';
import { Text } from './Text';
import './OnyxBanner.css';

const SEVERITY_MAP = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'error',
};

const SEVERITY_ICON = {
  info: 'info',
  success: 'check',
  warning: 'warning',
  error: 'warning',
};

function isTextish(child) {
  return typeof child === 'string' || typeof child === 'number';
}

function BannerCopy({ children }) {
  const nodes = React.Children.toArray(children).filter((child) => child != null && child !== false);
  if (!nodes.length) return null;
  if (nodes.every(isTextish)) {
    return <Text variant="body-m" className="onyx-banner__text">{nodes.join('')}</Text>;
  }
  return nodes.map((child, index) => (
    isTextish(child)
      ? <Text key={index} variant="body-m" className="onyx-banner__text">{child}</Text>
      : child
  ));
}

/**
 * Shared status / auth / intake banner. Replaces page-local banner CSS.
 */
export function OnyxBanner({
  children,
  severity = 'info',
  className = '',
  onClose,
  'data-testid': dataTestId = 'onyx-banner',
}) {
  const tone = SEVERITY_MAP[severity] || 'info';
  return (
    <Alert
      severity={tone}
      icon={<Icon name={SEVERITY_ICON[tone] || 'info'} size="sm" />}
      onClose={onClose}
      className={['onyx-banner', className].filter(Boolean).join(' ')}
      data-testid={dataTestId}
      data-severity={severity}
    >
      <BannerCopy>{children}</BannerCopy>
    </Alert>
  );
}

OnyxBanner.propTypes = {
  children: PropTypes.node,
  severity: PropTypes.oneOf(['info', 'success', 'warning', 'error']),
  className: PropTypes.string,
  onClose: PropTypes.func,
  'data-testid': PropTypes.string,
};
